import moment from 'moment'
import { rem } from 'polished'
import type { RefObject } from 'react'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { fireInteractionEvent } from 'api/googleTagManager'
import FormatCurrency from 'components/Common/FormatCurrency'
import { flightsArrivalDateSelect, flightsDepartureDateSelect } from 'analytics/eventDefinitions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import type { FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import Calendar from './Calendar'
import type {
  CalendarDay,
  CalendarDaySlot,
  CalendarSelection,
  CalendarSpan,
  CalendarSpanSegment,
} from './Calendar.types'
import CalendarDayCell from './CalendarDayCell'
import { clampCalendarDate, toCalendarDate, toMonthKey } from './calendarDate'
import type { TripStop } from 'tripPlanner/api/types'

import { StopCalendarDayMarkers, StopCalendarSpanBar } from './StopCalendarPresentation'
import { useAlignedMonth } from './calendarPanelLayout'
import { useTripCalendarMonthHeader } from './TripCalendarMonthHeader'
import TripCalendarShell from './TripCalendarShell'
import {
  buildBookedAccommodationSpans,
  buildStopSpans,
  buildTransportOverlays,
  type StopCalendarSpanData,
  type TransportMarker,
} from './stopCalendarOverlays'
import { resolveFlightDateSelection } from './flightCalendarSelection'
import useFlightCalendarPrices from './useFlightCalendarPrices'

const FeaturedDayStyles = styled.div`
  /* Marks the days a promoted flight deal actually covers. */
  [data-calendar-date].featured-deal:not(.state-disabled) {
    background: var(--lux-cal-range-fill);
    box-shadow: inset 0 -2px 0 var(--lux-cal-exclusive);
  }
`

/** Stable empty list, so a day with no journeys does not remount its marker row. */
const EMPTY_MARKERS: ReadonlyArray<TransportMarker> = []

const Fare = styled.div`
  margin-top: ${rem(2)};
  font-size: ${rem(11)};
  line-height: ${rem(14)};
  color: var(--lux-cal-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.cheapest {
    color: var(--lux-cal-cheap);
    font-weight: 600;
  }
`

interface Props {
  /** The flight leg this calendar is picking a date for. */
  flightId: string
  /** Every stop on the trip, drawn behind the dates as read-only context. */
  tripStops?: ReadonlyArray<TripStop>
  /**
   * The stop this flight arrives at, when the search came from one. Used to
   * decide which month to open on before a date has been picked.
   */
  contextStopId?: number
  /** Flight-deal date ranges, marked so a promoted deal can be found. */
  featuredRanges?: Array<{ start: Date, end: Date }>
  /** Earliest bookable day. */
  minDate: Date
  /** Latest bookable day. */
  maxDate: Date
  scrollContainerRef?: RefObject<HTMLElement | null>
  className?: string
}

/**
 * Choosing a flight date against the rest of the trip.
 *
 * The stops run behind the grid exactly as they do on the hotel calendar,
 * while each day keeps the cached return fare the standard flight calendar
 * shows — the bars sit in the grid's lane layer, above the day's own content,
 * so neither has to give way to the other.
 */
function TripContextFlightCalendar(props: Props) {
  const {
    flightId, tripStops, contextStopId, featuredRanges, minDate, maxDate,
    scrollContainerRef, className,
  } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const searchDispatch = useContext(FlightSearchWidgetDispatchContext)

  /*
   * No falling back to the first leg when the id finds nothing. The dates this
   * calendar writes are addressed by `flightId`, so a leg picked up by
   * position would be shown one leg's dates while editing another's — and an
   * empty flight list would take the destructure with it.
   */
  const currentFlight = useMemo(
    () => state.flights.find(flight => flight.id === flightId),
    [state.flights, flightId],
  )

  const checkinDate = currentFlight?.checkinDate
  const checkoutDate = currentFlight?.checkoutDate
  const departureAirport = currentFlight?.departureAirport
  const arrivalAirport = currentFlight?.arrivalAirport
  const fareType = state.fareType?.value as FlightsFareTypes | undefined

  /*
   * Which month to fetch fares for before anyone scrolls. The visible month
   * takes over from there, inside the hook.
   */
  const priceAnchorMonth = useMemo(() => {
    if (checkinDate) return toMonthKey(toCalendarDate(checkinDate.toDate()))
    if (state.calendarFocusMonth) return toMonthKey(toCalendarDate(state.calendarFocusMonth))
    return toMonthKey(toCalendarDate(minDate))
  }, [checkinDate, state.calendarFocusMonth, minDate])

  const prices = useFlightCalendarPrices({
    departureAirportCode: departureAirport?.airportCode,
    arrivalAirportCode: arrivalAirport?.airportCode,
    fareType,
    fareClass: state.fareCabin?.value,
    disabled: state.disableCalendarPricing,
    anchorMonth: priceAnchorMonth,
    rangeStartMonth: toMonthKey(toCalendarDate(minDate)),
    scrollContainerRef,
  })

  /*
   * The rendered extent stays put. It cannot follow the airline's sale window,
   * because that arrives with the fare response: changing it mid-session
   * rebuilds the month list underneath a scroller whose mounted window was
   * anchored against the old one, and the view jumps to a different month.
   */
  const from = useMemo(() => toCalendarDate(minDate), [minDate])
  const to = useMemo(() => toCalendarDate(maxDate), [maxDate])

  /*
   * The sale window is applied as availability instead. A route can be
   * narrower than the generic lead-time bounds, and a day outside it quotes a
   * fare that cannot be sold — so those days are closed off rather than
   * removed from the calendar.
   */
  const isOutsideSaleWindow = useCallback((date: string) => (
    (!!prices.earliestBookingDate && date < prices.earliestBookingDate) ||
    (!!prices.finalBookingDate && date > prices.finalBookingDate)
  ), [prices.earliestBookingDate, prices.finalBookingDate])

  const checkIn = useMemo(() => checkinDate && toCalendarDate(checkinDate.toDate()), [checkinDate])
  const checkOut = useMemo(() => checkoutDate && toCalendarDate(checkoutDate.toDate()), [checkoutDate])

  const stopSpans = useMemo(() => buildStopSpans(tripStops), [tripStops])

  /**
   * The month the grid should re-open on, as opposed to the one it is showing.
   * A date the traveller just picked must never re-anchor it — on a return
   * fare that would remount the calendar between the departure and the return.
   */
  const hintMonth = useMemo(() => {
    if (state.calendarFocusMonth) return toMonthKey(toCalendarDate(state.calendarFocusMonth))
    return contextStopId === undefined
      ? undefined
      : stopSpans.find(span => span.data?.stopId === contextStopId)?.from.slice(0, 7)
  }, [state.calendarFocusMonth, contextStopId, stopSpans])

  /**
   * Which month to open on.
   *
   * A date already picked wins, then whatever hint the search carries, then
   * the trip itself — the stop this flight arrives at, or failing that its
   * first dated stop. Without that last step a trip planned months out opens
   * on today, with the itinerary nowhere in sight.
   */
  const defaultMonth = useMemo(() => {
    const contextStopStart = contextStopId === undefined
      ? undefined
      : stopSpans.find(span => span.data?.stopId === contextStopId)?.from
    const anchor = checkIn ??
      (state.calendarFocusMonth ? toCalendarDate(state.calendarFocusMonth) : undefined) ??
      contextStopStart ??
      stopSpans[0]?.from ??
      from
    return toMonthKey(clampCalendarDate(anchor, from, to))
  }, [checkIn, state.calendarFocusMonth, contextStopId, stopSpans, from, to])

  const transport = useMemo(() => buildTransportOverlays(tripStops), [tripStops])

  const spans = useMemo<ReadonlyArray<CalendarSpan<StopCalendarSpanData>>>(() => [
    ...stopSpans,
    ...buildBookedAccommodationSpans(tripStops),
    ...transport.spans,
  ], [stopSpans, tripStops, transport])

  const selection = useMemo<CalendarSelection>(() => ({
    mode: 'range',
    range: checkIn ? { from: checkIn, to: checkOut } : undefined,
  }), [checkIn, checkOut])

  /*
   * The month to bring to the top: whatever the panel opened on, until an
   * external hint says otherwise. Never the picked date.
   *
   * The hint is kept once given, so a hint that goes away — the search
   * clearing its focus month once a leg is chosen — cannot read as a new
   * month to open on. Reacting to that would remount the grid and lose every
   * month the traveller had scrolled through, between the two legs of a
   * return.
   */
  const openingMonthRef = useRef(defaultMonth)
  const lastHintMonthRef = useRef(hintMonth)
  if (hintMonth) lastHintMonthRef.current = hintMonth
  useAlignedMonth(lastHintMonthRef.current ?? openingMonthRef.current, scrollContainerRef)

  const onSelect = useCallback((_next: CalendarSelection, day: CalendarDay) => {
    // Nothing to write a date to, so a click cannot mean anything yet.
    if (!currentFlight) return
    if (isOutsideSaleWindow(day.date)) return
    const pickedDate = moment(day.date)
    const outcome = resolveFlightDateSelection({
      fareType,
      selectingDateType: state.selectingDateType,
      checkinDate,
      checkoutDate,
      pickedDate,
      pickedDayCachedPrice: prices.cachedPriceFor(day.date),
      checkinDayCachedPrice: checkinDate
        ? prices.cachedPriceFor(checkinDate.format(ISO_DATE_FORMAT))
        : undefined,
    })

    if (outcome.checkIn) {
      searchDispatch({
        type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
        id: flightId,
        date: outcome.checkIn.date,
        quotedPrice: outcome.checkIn.quotedPrice,
      })
    }
    if (outcome.checkOut) {
      searchDispatch({
        type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
        id: flightId,
        date: outcome.checkOut.date,
        quotedPrice: outcome.checkOut.quotedPrice,
      })
    }

    fireInteractionEvent(
      outcome.chose === 'arrival' ? flightsArrivalDateSelect() : flightsDepartureDateSelect(),
    )

    if (outcome.menu.action === 'close') {
      searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id: flightId, activeMenu: undefined })
    } else if (outcome.menu.action === 'open') {
      searchDispatch({
        type: FlightSearchWidgetActions.SET_ACTIVE_MENU,
        id: flightId,
        activeMenu: StandaloneFlightMenu.date,
        selectingDateType: outcome.menu.selecting,
      })
    }
  }, [
    fareType, state.selectingDateType, checkinDate, checkoutDate, prices,
    searchDispatch, flightId, currentFlight, isOutsideSaleWindow,
  ])

  const renderDay = useCallback((slot: CalendarDaySlot<never, StopCalendarSpanData>) => {
    const unavailable = isOutsideSaleWindow(slot.day.date)
    // Agent Hub runs its own promotions, so deal ranges are not marked there.
    const isFeatured = !isAgentHubEnabled && !!featuredRanges?.some(
      (range: { start: Date, end: Date }) =>
        moment(slot.day.date).isBetween(range.start, range.end, 'days', '[]'),
    )
    const cachedPrice = prices.cachedPriceFor(slot.day.date)
    // The cached figure covers both legs; the search quotes one.
    const perLegPrice = cachedPrice === undefined ? undefined : Math.floor(cachedPrice / 2)

    return <CalendarDayCell
      date={slot.day.date}
      day={slot.day.day}
      state={slot.state}
      isAdjacentMonth={slot.day.isAdjacentMonth}
      isToday={slot.day.isToday}
      laneCount={slot.laneCount}
      isUnavailable={unavailable}
      // Out on the departure day, back on the return one — both half days.
      halfDayRangeEdges
      className={isFeatured ? 'featured-deal' : undefined}
      disabled={slot.day.isOutOfBounds || unavailable}
      onSelect={slot.select}
      data-testid={`day-${slot.day.date}`}
    >
      {/* Same rule as the standard flight calendar: no fare while it loads. */}
      {prices.arePricesVisible && !prices.loading && !!perLegPrice && (
        <Fare className={prices.isCheapestInMonth(slot.day.date) ? 'cheapest' : undefined}>
          <FormatCurrency value={perLegPrice} />
        </Fare>
      )}
      <StopCalendarDayMarkers markers={transport.markersByDate.get(slot.day.date) ?? EMPTY_MARKERS} />
    </CalendarDayCell>
  }, [prices, featuredRanges, isOutsideSaleWindow, transport])

  /** Where the grid stops, so a stop it cuts off is still named. */

  const renderSpan = useCallback((segment: CalendarSpanSegment<StopCalendarSpanData>) => (
    <StopCalendarSpanBar segment={segment} boundaryGap="tight" />
  ), [])

  const renderMonthHeader = useTripCalendarMonthHeader(true)

  return <TripCalendarShell className={className} $density="fit-month" as={FeaturedDayStyles}>
    <Calendar<never, StopCalendarSpanData>
      key={lastHintMonthRef.current}
      label={intl.formatMessage({
        id: 'TSBx75J9rlZ-yxy4_W3P',
        defaultMessage: 'Choose your flight dates, with the rest of your trip shown for reference',
        description: 'Accessible name for the flight search calendar inside the trip planner, which also shows the other stops on the trip',
      })}
      from={from}
      to={to}
      spans={spans}
      selectionMode="range"
      value={selection}
      onSelect={onSelect}
      renderDay={renderDay}
      renderSpan={renderSpan}
      renderMonthHeader={renderMonthHeader}
      defaultMonth={defaultMonth}
      layout="scroll"
      scrollContainerRef={scrollContainerRef}
    />
  </TripCalendarShell>
}

export default TripContextFlightCalendar
